// UI slice for the Flow2 inline Goals table (FF GOALS_VIEW_REVAMP_FE_AEX-3428).
// Holds the transient "which row is being created/edited" state in redux rather than
// in the component. This is required because ClientJourneyFlow2 re-defines its step
// content components on every render, so subscribing to this slice (to lock navigation)
// remounts the Goals step; keeping the edit state here lets it survive that remount.
// `hasUnsavedGoalChanges` (used to lock Continue + stepper) is simply `editingRowKey != null`.
// This slice is excluded from redux-persist (see store.ts) so it always starts clean.
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type GoalEditRowType = 'income' | 'wealth' | 'retirement';

interface GoalEditState {
    /** null when no row is open; a goalId when editing; the synthetic new-row id when adding. */
    editingRowKey: string | null;
    /** The goal type of the in-progress "add" row, or null when not adding. */
    addGoalType: GoalEditRowType | null;
    /**
     * True once the in-progress add/edit row has actual unsaved changes (dirty). `editingRowKey`
     * (row open) disables "Continue to Next Step"; this dirty flag decides whether navigating away
     * via the stepper shows the Stay/Leave confirmation (no edits => leave silently, no modal).
     */
    hasUnsavedGoalEdits: boolean;
    /** Save-failure banner; kept here so it survives the Goals-step remount mid-save. */
    saveErrorType: GoalSaveErrorType;
}

export type GoalSaveErrorType = 'generic' | 'stale' | null;

const initialState: GoalEditState = {
    editingRowKey: null,
    addGoalType: null,
    hasUnsavedGoalEdits: false,
    saveErrorType: null
};

const goalEditSlice = createSlice({
    name: 'goalEdit',
    initialState,
    reducers: {
        setGoalEdit: (
            state,
            action: PayloadAction<{ editingRowKey: string | null; addGoalType: GoalEditRowType | null }>
        ) => {
            state.editingRowKey = action.payload.editingRowKey;
            state.addGoalType = action.payload.addGoalType;
            state.hasUnsavedGoalEdits = false;
            state.saveErrorType = null;
        },
        setGoalEditingRowKey: (state, action: PayloadAction<string | null>) => {
            state.editingRowKey = action.payload;
            if (action.payload === null) {
                state.addGoalType = null;
                state.hasUnsavedGoalEdits = false;
                state.saveErrorType = null;
            }
        },
        setHasUnsavedGoalEdits: (state, action: PayloadAction<boolean>) => {
            state.hasUnsavedGoalEdits = action.payload;
        },
        setGoalSaveErrorType: (state, action: PayloadAction<GoalSaveErrorType>) => {
            state.saveErrorType = action.payload;
        },
        clearGoalEdit: (state) => {
            state.editingRowKey = null;
            state.addGoalType = null;
            state.hasUnsavedGoalEdits = false;
            state.saveErrorType = null;
        }
    }
});

export const { setGoalEdit, setGoalEditingRowKey, setHasUnsavedGoalEdits, setGoalSaveErrorType, clearGoalEdit } =
    goalEditSlice.actions;

export default goalEditSlice.reducer;
